import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'
import { useAppSelector } from '@/store/hooks'
import type { CourseWithoutBlocks } from '@/types/course'
import { cn } from '@/lib/utils'
import { useIsCourseAuthor } from '@/features/editor/hooks/useIsCourseAuthor'
import { useIsCourseFrozen } from '@/features/moderation/useCourseModeration'
import { useIsCourseTextLocked } from '../studio/useBlocksInRun'
import { BasicInfoSection } from './BasicInfoSection'
import { CourseAccessBlock } from './CourseAccessBlock'
import { DesignSystemSection } from './DesignSystemSection'
import { PricingSection } from './PricingSection'
import { MapSection } from './MapSection'
import { LanguagesSection } from './LanguagesSection'
import { CertificatesSection } from './CertificatesSection'
import { SettingsLangSwitcher } from './SettingsLangSwitcher'
import { TABS } from './tabs'
import type { TabKey } from './tabs'
import type { ViolationGo } from '../publish/violationPlace'
import type { useCourseSettingsForm } from './useCourseSettingsForm'
import { useUiLang } from '@/i18n'

type Form = ReturnType<typeof useCourseSettingsForm>

interface Props {
  course: CourseWithoutBlocks
  activeTab: TabKey
  local: Form['local']
  setLocal: Dispatch<SetStateAction<Form['local']>>
  save: Form['save']
  /** Заголовок и подпись вкладки. На странице их нет: имя вкладки уже стоит над ней. */
  showHeading?: boolean
  /** «Перейти» у нарушения модерации (блок во вкладке «Доступ»). */
  onNavigate: (target: ViolationGo) => void
}

/**
 * Содержимое выбранной вкладки настроек курса: заголовок, описание и сама секция.
 * Один и тот же код у окна в редакторе (`CourseSettingsDialog`) и у страницы
 * настроек (`CourseSettingsPage`) — вкладки не расходятся.
 */
export const CourseSettingsContent = ({
  course,
  activeTab,
  local,
  setLocal,
  save,
  showHeading = true,
  onNavigate,
}: Props) => {
  // Подписка на язык: заголовок вкладки читается геттером `TABS` (messagesNow).
  useUiLang()
  const isCourseAuthor = useIsCourseAuthor(course)
  // §7.5: пока идёт перевод, текст курса правит прогон — тот же гейт, что у
  // полей урока, название и описание курса спека называет поимённо.
  const isTextLocked = useIsCourseTextLocked(course.id)
  const isFrozen = useIsCourseFrozen(course.id)
  const current = TABS.find((t) => t.key === activeTab) ?? TABS[0]
  // Язык полей «Основного» — свой: переключатель над ними меняет только их, а
  // язык правки редактора (шапка, холст) остаётся. Засевается языком правки и
  // следует за ним, если его сменили в шапке.
  const editorLang = useAppSelector((s) => s.editor.contentLang)
  const [fieldsLang, setFieldsLang] = useState(editorLang)
  useEffect(() => setFieldsLang(editorLang), [editorLang])
  // «Перейти» у незаполненного поля открывает «Основное» сразу на его языке.
  const requestedLang = useAppSelector((s) => s.editor.settingsLang)
  // Номер запроса в зависимостях: повторный «Перейти» на тот же язык после ручной
  // смены переключателя не меняет `requestedLang`, но обязан поставить его снова.
  const requestedSeq = useAppSelector((s) => s.editor.settingsLangSeq)
  useEffect(() => {
    if (requestedLang) setFieldsLang(requestedLang)
  }, [requestedLang, requestedSeq])

  return (
    <>
      {showHeading || activeTab === 'basic' ? (
        <div
          className={cn(
            'flex items-start gap-4',
            // На странице переключатель языка стоит отдельной строкой между вкладками и
            // названием курса; в окне — справа от заголовка вкладки.
            showHeading ? 'mb-4 justify-between' : 'mb-5 justify-start',
          )}
        >
          {showHeading ? (
            <div>
              <h3 className="text-lg font-semibold leading-none tracking-tight">{current.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{current.description}</p>
            </div>
          ) : null}
          {/* Только во вкладке «Основное» (п.9 правок 29 августа): двуязычные
            поля есть лишь здесь, а над «Монетизацией» или «Картой»
            переключатель обещал бы языковой слот, которого у их полей нет. */}
          {activeTab === 'basic' ? (
            <SettingsLangSwitcher
              course={course}
              draft={local}
              value={fieldsLang}
              onChange={setFieldsLang}
            />
          ) : null}
        </div>
      ) : null}
      {activeTab === 'basic' ? (
        <BasicInfoSection
          lang={fieldsLang}
          local={local}
          setLocal={setLocal}
          save={save}
          isCourseAuthor={isCourseAuthor}
          isTextLocked={isTextLocked}
          isFrozen={isFrozen}
        />
      ) : null}
      {activeTab === 'access' ? (
        <div className="space-y-5">
          <CourseAccessBlock course={course} local={local} onNavigate={onNavigate} />
        </div>
      ) : null}
      {activeTab === 'design' ? (
        <DesignSystemSection
          local={local}
          setLocal={setLocal}
          save={save}
          isCourseAuthor={isCourseAuthor}
          isFrozen={isFrozen}
        />
      ) : null}
      {activeTab === 'pricing' ? (
        <PricingSection
          course={course}
          local={local}
          setLocal={setLocal}
          save={save}
          isCourseAuthor={isCourseAuthor}
        />
      ) : null}
      {activeTab === 'map' ? (
        <MapSection
          course={course}
          save={save}
          isCourseAuthor={isCourseAuthor}
          isFrozen={isFrozen}
        />
      ) : null}
      {activeTab === 'languages' ? <LanguagesSection course={course} /> : null}
      {activeTab === 'certificates' ? <CertificatesSection /> : null}
    </>
  )
}
